import React, { useState, useMemo, useEffect } from 'react';
import { Box, Tab, Tabs } from '@mui/material';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import Grid from '@mui/material/Unstable_Grid2/Grid2';
import useStore from '../../states/store';
import TabPanel from '../../components/TabPanel';
import Filter from './components/Filter';
import Table from './components/Table';
import useCWPAminContext from './context/useCWPAminContext';
import { DashboardProvider } from '../Dashboard/useDashboard';
import {
  postAppointmentList,
  postAppointmentDelete,
  postAppointmentRecover,
} from '../../api/cwpMaintenance';

export default function CWPMaintenanceAdmin() {
  const auth = useStore((state) => state.auth);
  const date = new Date();
  const monthsOptions = [
    { label: 'ALL', value: null },
    { label: 'January', value: '01' },
    { label: 'February', value: '02' },
    { label: 'March', value: '03' },
    { label: 'April', value: '04' },
    { label: 'May', value: '05' },
    { label: 'June', value: '06' },
    { label: 'July', value: '07' },
    { label: 'August', value: '08' },
    { label: 'September', value: '09' },
    { label: 'October', value: '10' },
    { label: 'November', value: '11' },
    { label: 'December', value: '12' },
  ];
  const yearOptions = [];

  /* eslint-disable no-plusplus */
  for (let i = 2010; i <= date.getFullYear(); i++) {
    yearOptions.push({ label: i.toString(), value: i.toString() });
  }
  const defaultFilter = {
    department: { label: 'ALL', value: null },
    manager: { label: 'ALL', value: null },
    kasssar: { label: 'ALL', value: null },
    month: [monthsOptions[date.getMonth() + 1]],
    year: { label: date.getFullYear().toString(), value: date.getFullYear().toString() },
  };

  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [cwpData, setCwpData] = useState([]);
  const [rowsSelected, setRowsSelected] = useState([]);
  const [filterForm, setFilterForm] = useState(defaultFilter);
  const [clearFilter, setClearFilter] = useState(false);

  const handleChangeActiveTab = (event, newValue) => {
    setActiveTab(newValue);
  };

  const searchCwp = async () => {
    try {
      setLoading(true);

      const statusType = {
        department: filterForm?.department?.value,
        manager_id: auth.designation !== 'admin' ? auth.user_id : filterForm?.manager?.value,
        rep_id: filterForm?.kasssar?.value,
        month:
          filterForm?.month.filter((item) => item.value === null).length > 0
            ? null
            : filterForm?.month.map((row) => row.value).sort(),
        deleted_status: activeTab,
      };
      const response = await postAppointmentList(statusType);
      const { code, data } = response.data;

      if (code === '200') {
        setCwpData(data);
      }

      setLoading(false);
    } catch (error) {
      toast.error(error.message);
    }
  };
  const clearCwp = () => {
    setFilterForm(defaultFilter);
    setClearFilter(true);
  };

  const handleRecoverAllSelected = (row = null, isall = null) => {
    Swal.fire({
      icon: 'warning',
      title: `Are you sure you want to recover ${
        isall === true ? 'all selected' : ''
      } appointment(s)`,
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: '<small style="color:#fff"> Confirm </small>',
      cancelButtonText: '<small style="color:#fff"> Cancel </small>',
    }).then((response) => {
      if (response.isConfirmed) {
        const cwpDetailsForm =
          isall === true
            ? {
                data: rowsSelected?.map((rw) => ({ appointment_id: rw.appointment_id })),
              }
            : {
                data: [{ appointment_id: row }],
              };

        try {
          postAppointmentRecover(cwpDetailsForm).then((res) => {
            const { code, message } = res.data;

            if (code === '200') {
              toast.success(message);
              searchCwp();
            }
          });
        } catch (error) {
          toast.error(error.message);
        }
      }
    });
  };

  const handleDeleteAllSelected = (row = null, isall = null) => {
    Swal.fire({
      icon: 'warning',
      title: `Are you sure you want to delete all appointments`,
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: '<small style="color:#fff"> Confirm </small>',
      cancelButtonText: '<small style="color:#fff"> Cancel </small>',
    }).then((response) => {
      if (response.isConfirmed) {
        const cwpDetailsForm =
          isall === true
            ? {
                data: rowsSelected?.map((rw) => ({ appointment_id: rw.appointment_id })),
              }
            : {
                data: [{ appointment_id: row }],
              };

        try {
          postAppointmentDelete(cwpDetailsForm).then((res) => {
            const { code, message } = res.data;

            if (code === '200') {
              toast.success(message);
              searchCwp();
            }
          });
        } catch (error) {
          toast.error(error.message);
        }
      }
    });
  };
  useEffect(() => {
    searchCwp();
  }, [activeTab, clearFilter]);

  return (
    <DashboardProvider>
      <useCWPAminContext.Provider
        value={useMemo(
          () => ({
            auth,
            activeTab,
            loading,
            filterForm,
            monthsOptions,
            cwpData,
            rowsSelected,
            setFilterForm,
            searchCwp,
            clearCwp,
            setRowsSelected,
            handleDeleteAllSelected,
            handleRecoverAllSelected,
          }),
          [activeTab, filterForm, loading, cwpData, rowsSelected],
        )}
      >
        <Box sx={{ backgroundColor: '#fff', p: 1, boxShadow: 3 }}>
          <Filter />
        </Box>
        <Box padding={4}>
          <Box>
            <Box>
              <Grid container spacing={2}>
                <Grid xs={12} sm={12} md={12}>
                  <Box sx={{ backgroundColor: '#fff' }} mt={4}>
                    <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                      <Tabs
                        value={activeTab}
                        onChange={handleChangeActiveTab}
                        aria-label='basic tabs example'
                      >
                        <Tab
                          label='Appointments'
                          id='cwp-appointments'
                          aria-controls='cwp-appointments'
                        />

                        <Tab
                          label='Deleted Appointments'
                          id='cwp-deleted-appointments'
                          aria-controls='cwp-deleted-appointments'
                        />

                        <Tab
                          label='Recently Deleted Appointments'
                          id='cwp-recently-deleted-appointments'
                          aria-controls='cwp-recently-deleted-appointments'
                        />
                      </Tabs>
                    </Box>
                    <TabPanel value={activeTab} index={activeTab}>
                      <Table />
                    </TabPanel>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Box>
        </Box>
      </useCWPAminContext.Provider>
    </DashboardProvider>
  );
}
